import { Formik, Form } from "formik";
import _ from "lodash";
import { useConfirm } from "material-ui-confirm";
import { useState } from "react";
import { Link } from "react-router-dom";
import * as yup from "yup";

import {
  Card,
  CardHeader,
  CardContent,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  makeStyles
} from "@material-ui/core";

import FormikErrorBanner from "src/components/form/FormikErrorBanner";
import FormikSwitchField from "src/components/form/FormikSwitchField";
import FormikTextField from "src/components/form/FormikTextField";
import {
  JobStatus,
  TenantExportBigQuerySettingsCreateInput,
  TenantExportBigQuerySettingsUpdateInput
} from "src/graphql/generated/globalTypes";
import { listTenantExportConfigs_listTenantExportConfigs } from "src/graphql/generated/listTenantExportConfigs";
import {
  tenantExportConfigCreateBigQuery,
  tenantExportConfigCreateBigQueryVariables
} from "src/graphql/generated/tenantExportConfigCreateBigQuery";
import {
  tenantExportConfigDelete,
  tenantExportConfigDeleteVariables
} from "src/graphql/generated/tenantExportConfigDelete";
import {
  tenantExportConfigUpdateBigQuery,
  tenantExportConfigUpdateBigQueryVariables
} from "src/graphql/generated/tenantExportConfigUpdateBigQuery";
import {
  CREATE_BIGQUERY_TENANT_EXPORT_CONFIG,
  DELETE_TENANT_EXPORT_CONFIG,
  UPDATE_BIGQUERY_TENANT_EXPORT_CONFIG
} from "src/graphql/tenantExportConfigs";
import { getErrorMessage, graphqlErrorHasCode } from "src/util/errors";
import useMutation from "src/util/useMutation";

type ExportConfig = listTenantExportConfigs_listTenantExportConfigs;

type FormValues = {
  gcsBucket: string;
  gcsBucketPrefix: string;
  dataset: string;
  credentialsJSON: string;
  addTenantScoping: boolean;
};

const useStyles = makeStyles(theme => ({
  button: {
    marginRight: theme.spacing(1)
  },
  buttonsWrapper: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between"
  }
}));

function BigQueryExportConfigDialog({
  onClose,
  exportConfig,
  handleSubmitCreate,
  handleSubmitUpdate
}: {
  onClose: () => void;
  exportConfig: ExportConfig | undefined;
  handleSubmitCreate: (
    settings: TenantExportBigQuerySettingsCreateInput
  ) => Promise<void>;
  handleSubmitUpdate: (
    settings: TenantExportBigQuerySettingsUpdateInput
  ) => Promise<void>;
}) {
  const [saveErrors, setSaveErrors] = useState<string | null>(null);

  const isUpdate = !!exportConfig;

  const credentialsValidator = yup
    .string()
    .json([
      "type",
      "project_id",
      "private_key_id",
      "private_key",
      "client_email",
      "client_id",
      "auth_uri",
      "token_uri",
      "auth_provider_x509_cert_url",
      "client_x509_cert_url"
    ]);

  const schema = yup.object().shape({
    gcsBucket: yup.string().required("Required"),
    gcsBucketPrefix: yup.string(),
    dataset: yup.string().required("Required"),
    credentialsJSON: isUpdate
      ? credentialsValidator
      : credentialsValidator.required("Required"),
    addTenantScoping: yup.bool()
  });

  const initialValues: FormValues = {
    gcsBucket: exportConfig?.bigQuerySettings?.gcsBucket ?? "",
    gcsBucketPrefix: exportConfig?.bigQuerySettings?.gcsBucketPrefix ?? "",
    dataset: exportConfig?.bigQuerySettings?.dataset ?? "",
    addTenantScoping: !!exportConfig?.bigQuerySettings?.addTenantScoping,
    credentialsJSON: ""
  };

  const handleSubmit = async (values: FormValues) => {
    setSaveErrors(null);

    try {
      if (isUpdate) {
        await handleSubmitUpdate({
          // addTenantScoping cannot be updated
          ..._.omit(values, ["addTenantScoping"]),
          credentialsJSON:
            values.credentialsJSON.trim().length > 0
              ? values.credentialsJSON
              : undefined
        });
      } else {
        await handleSubmitCreate(values);
      }
    } catch (e: any) {
      setSaveErrors(e.message);
    }
  };

  return (
    <Dialog open onClose={onClose}>
      <DialogTitle>
        {isUpdate
          ? "Edit BigQuery Export Configuration"
          : "Configure BigQuery Export"}
      </DialogTitle>
      <Formik
        initialValues={initialValues}
        validationSchema={schema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting }) => (
          <Form>
            <DialogContent>
              <FormikErrorBanner
                additionalErrors={saveErrors}
                noScrollOnError
              />
              <FormikTextField fullWidth name="gcsBucket" label="GCS Bucket" />
              <FormikTextField
                fullWidth
                name="gcsBucketPrefix"
                label="GCS Bucket Object Prefix (optional)"
              />
              <FormikTextField
                fullWidth
                name="dataset"
                label="BigQuery Dataset Name"
              />
              <FormikTextField
                fullWidth
                multiline
                name="credentialsJSON"
                label={
                  isUpdate
                    ? "Credentials JSON (leave blank to keep current)"
                    : "Credentials JSON"
                }
                inputProps={{
                  // blur text: https://stackoverflow.com/a/66618206
                  style: {
                    color: "transparent",
                    textShadow: "0 0 8px rgba(0,0,0,0.5)"
                  }
                }}
                rows={4}
              />
              <FormikSwitchField
                name="addTenantScoping"
                label="Add tenant scoped columns"
                switchProps={{
                  disabled: isUpdate
                }}
                helperText={`Prepends tenant_id and tenant_slug columns to the export. Useful for configuring syncs from multiple tenants to a single dataset.${
                  isUpdate
                    ? " NOTE: this cannot be updated on existing syncs."
                    : ""
                }`}
              />
            </DialogContent>
            <DialogActions>
              <Button disabled={isSubmitting} onClick={onClose} color="primary">
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                color="primary"
                variant="contained"
              >
                {isSubmitting ? "Saving..." : "Save"}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
}

function BigQueryExportDeleteButton({
  onDelete
}: {
  onDelete: () => Promise<any>;
}): JSX.Element {
  const [loading, setLoading] = useState(false);
  const confirm = useConfirm();

  return (
    <Button
      disabled={loading}
      color="secondary"
      onClick={async () => {
        try {
          await confirm({
            title: "Disable BigQuery Export?",
            description:
              "Are you sure you want to disable BigQuery exports? Any data in BigQuery will remain there, but no new exports will be run. You'll need to re-enter your credentials to re-enable exports."
          });
        } catch {
          // user cancelled
          return;
        }

        setLoading(true);
        await onDelete();
        setLoading(false);
      }}
    >
      {loading ? "Disabling..." : "Disable"}
    </Button>
  );
}

export default function BigQueryTenantExportConfig({
  exportConfig
}: {
  exportConfig: ExportConfig | undefined;
}): JSX.Element {
  const classes = useStyles();
  const [dialogOpen, setDialogOpen] = useState(false);

  const [createExportConfig] = useMutation<
    tenantExportConfigCreateBigQuery,
    tenantExportConfigCreateBigQueryVariables
  >(CREATE_BIGQUERY_TENANT_EXPORT_CONFIG, {
    refetchQueries: ["listTenantExportConfigs"],
    awaitRefetchQueries: true
  });

  async function handleSubmitCreate(
    settings: TenantExportBigQuerySettingsCreateInput
  ) {
    const { errors } = await createExportConfig({
      variables: { name: "BigQuery", settings }
    });

    if (errors) {
      if (graphqlErrorHasCode("INVALID_TENANT_EXPORT_CONFIG", errors)) {
        throw new Error(getErrorMessage(errors));
      } else {
        console.error(errors);
        throw new Error("Error configuring BigQuery export");
      }
    } else {
      setDialogOpen(false);
    }
  }

  const [updateExportConfig] = useMutation<
    tenantExportConfigUpdateBigQuery,
    tenantExportConfigUpdateBigQueryVariables
  >(UPDATE_BIGQUERY_TENANT_EXPORT_CONFIG, {
    refetchQueries: ["listTenantExportConfigs"],
    awaitRefetchQueries: true
  });

  async function handleSubmitUpdate(
    settings: TenantExportBigQuerySettingsUpdateInput
  ) {
    const { errors } = await updateExportConfig({
      variables: { id: exportConfig!.id, name: "BigQuery", settings }
    });

    if (errors) {
      if (graphqlErrorHasCode("INVALID_TENANT_EXPORT_CONFIG", errors)) {
        throw new Error(getErrorMessage(errors));
      } else {
        console.error(errors);
        throw new Error("Error configuring BigQuery export");
      }
    } else {
      setDialogOpen(false);
    }
  }

  const [deleteExportConfig] = useMutation<
    tenantExportConfigDelete,
    tenantExportConfigDeleteVariables
  >(DELETE_TENANT_EXPORT_CONFIG, {
    refetchQueries: ["listTenantExportConfigs"],
    awaitRefetchQueries: true,
    onErrorSnackbarMessage: "Failed to disable BigQuery export"
  });

  function renderStatus() {
    if (!exportConfig) {
      return "Not configured";
    }

    if (!exportConfig.lastRunJob) {
      return "Has not run yet";
    }

    const startedAt = new Date(
      exportConfig.lastRunJob.createTime
    ).toLocaleString();

    switch (exportConfig.lastRunJob.status) {
      case JobStatus.CREATED:
      case JobStatus.QUEUED:
      case JobStatus.RUNNING:
        return `Running now (started at ${startedAt})`;
      case JobStatus.DONE:
        return `Last run succeeded (started at ${startedAt})`;
      case JobStatus.FAILED:
        return `Last run failed (started at ${startedAt}): ${exportConfig.lastRunJob.errorMessage}`;
    }
  }

  return (
    <Card>
      <CardHeader title="BigQuery Export" />
      <CardContent>
        <p>
          BigQuery Exports allow you to export all of your data to Google
          Cloud's BigQuery.{" "}
          <a
            href="https://scaletowin.freshdesk.com/support/solutions/articles/66000501840-set-up-a-bigquery-integration-for-your-dialer-organization/"
            target="_blank"
            rel="noopener noreferrer"
          >
            Learn More
          </a>
          .
        </p>
        <p>
          <strong>Status:</strong> {renderStatus()}
        </p>
        <div className={classes.buttonsWrapper}>
          <div>
            <Button
              onClick={() => setDialogOpen(true)}
              variant="contained"
              color="primary"
              className={classes.button}
            >
              {exportConfig ? "Edit Configuration" : "Configure"}
            </Button>
            {exportConfig && (
              <BigQueryExportDeleteButton
                onDelete={() =>
                  deleteExportConfig({ variables: { id: exportConfig.id } })
                }
              />
            )}
          </div>
          <div>
            <Link
              component={Button}
              to="/organization/exports/data-dictionary"
              color="primary"
            >
              Data Dictionary
            </Link>
          </div>
        </div>

        {dialogOpen && (
          <BigQueryExportConfigDialog
            onClose={() => setDialogOpen(false)}
            exportConfig={exportConfig}
            handleSubmitCreate={handleSubmitCreate}
            handleSubmitUpdate={handleSubmitUpdate}
          />
        )}
      </CardContent>
    </Card>
  );
}
